import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '../../../components/ui/Button';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import { fetchChildren, fetchView } from '../orgChartApi';
import { chainForDisplay } from '../orgChartModel';

// Kişi Odaklı görünüm (spec 2026-10-24 §6 — Microsoft Org Explorer deseni): üstte yönetici zinciri, ortada odak kişi,
// altta doğrudan bağlılar ızgarası, sağda ekip arkadaşları. Tuval yok; içerik kendi alanında dikey kayar.
// Veri: view?focus=<id>&depth=1 (zincir + odak + bağlılar) ve odağın üstünün çocukları (ekip arkadaşları).

const NARROW = 1100;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 28px 24px 60px;
  font-family: 'Nunito Sans', sans-serif;
  color: #1F2A37;
`;

const Wrap = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 28px;
  @media (max-width: ${NARROW - 1}px) { grid-template-columns: minmax(0, 1fr); }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
`;

const ChainCard = styled.button`
  width: 300px;
  max-width: 100%;
  background: #fff;
  border: 1px solid #E6EAF0;
  border-radius: 12px;
  padding: 8px 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font: 13px 'Nunito Sans', sans-serif;
  color: inherit;
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
  small { display: block; color: ${({ theme }) => theme.colors.text.muted}; font-size: 12px; }
`;

const VLine = styled.div`
  width: 2px;
  height: ${({ $h }) => $h || 16}px;
  background: ${({ theme }) => theme.colors.orgChart.line};
`;

const MoreChain = styled.button`
  border: 1px dashed #C3CBD5;
  background: #fff;
  border-radius: 999px;
  padding: 3px 12px;
  font: 700 12px 'Nunito Sans', sans-serif;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
`;

const Hero = styled.div`
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #E6EAF0;
  border-radius: 18px;
  padding: 22px;
  display: flex;
  gap: 18px;
  align-items: center;
  box-shadow: 0 0 0 4px rgba(2, 145, 239, 0.12), 0 10px 30px rgba(15, 23, 42, 0.08);
  h2 { margin: 0; font-size: 22px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
`;

const Stats = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  b { color: #1F2A37; font-size: 16px; display: block; }
`;

const Section = styled.div`
  width: 100%;
  margin-top: ${({ $flush }) => ($flush ? 0 : 30)}px;
`;

const SectionHead = styled.div`
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 12px;
  span { color: ${({ theme }) => theme.colors.text.muted}; font-weight: 600; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
`;

const GridCard = styled.button`
  background: ${({ $vacant }) => ($vacant ? '#FAFBFC' : '#fff')};
  border: 1px ${({ $vacant }) => ($vacant ? 'dashed' : 'solid')} #E6EAF0;
  border-radius: 14px;
  padding: 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font: 13px 'Nunito Sans', sans-serif;
  color: inherit;
  min-width: 0;
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08); }
`;

const Who = styled.div`
  min-width: 0;
  flex: 1;
  div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  div + div { font-size: 12px; color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Count = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.muted};
  background: #F5F7FA;
  border-radius: 999px;
  padding: 3px 8px;
  white-space: nowrap;
`;

const Side = styled.div`
  background: #fff;
  border: 1px solid #E6EAF0;
  border-radius: 16px;
  padding: 16px;
  align-self: start;
`;

const PeerRow = styled.button`
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: 13px 'Nunito Sans', sans-serif;
  color: inherit;
  &:hover { background: #F5F7FA; }
  small { display: block; color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Avatar = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: ${({ $size }) => Math.round($size / 3)}px;
  background: ${({ $color }) => $color};
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Chip = styled.span`
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}1f`};
`;

const You = styled.span`
  font-size: 10px;
  font-weight: 800;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  border-radius: 5px;
  padding: 1px 6px;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #8C96A0;
`;

function initials(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toLocaleUpperCase('tr') || '?';
}

const vacantOf = (d) => d?.isAssigned === false || d?.isVacant === true;

function Person({ data, size, color }) {
  const vacant = vacantOf(data);
  return (
    <Avatar $size={size} $color={vacant ? '#C3CBD5' : color}>
      {!vacant && data?.profilePicture ? <img src={data.profilePicture} alt="" /> : (vacant ? '?' : initials(data?.userName))}
    </Avatar>
  );
}

export default function PersonView({
  focus, colorOf = () => '#A0A8B4', onFocus, onShowInTree, onNotFound, renderScopeTools, onLoaded,
}) {
  const { t } = useTranslation();
  const [state, setState] = useState({ status: 'loading' });
  const [peers, setPeers] = useState({ items: [], total: 0, page: 0 });
  const [chainOpen, setChainOpen] = useState(false);
  const seq = useRef(0);
  // Bir önceki GÖRÜNÜR odak: sunucuya `via` olarak gider — projeksiyon gecikirken üst zincirdeki bir kişiye tıklamak 404
  // vermesin (görünür bir düğümün ataları yapısal olarak görünür; spec 2026-10-15 §6.1).
  const lastFocusId = useRef(null);

  const label = (d) => (vacantOf(d) ? t('orgChart.vacantPosition') : (d?.userName || '-'));

  const loadPeers = useCallback(async (parentId, focusId, page, mine) => {
    const res = await fetchChildren(parentId, { page, via: focusId });
    if (mine !== seq.current || res.state !== 'ok') return;
    const items = (res.data.nodes || []).filter((n) => n.id !== focusId).map((n) => n.data);
    setPeers((prev) => ({
      items: page > 1 ? prev.items.concat(items) : items,
      total: Math.max(0, (res.data.totalCount || 0) - 1),
      page,
    }));
  }, []);

  useEffect(() => {
    const mine = ++seq.current;
    setState((prev) => (prev.status === 'ok' ? prev : { status: 'loading' }));
    setPeers({ items: [], total: 0, page: 0 });
    setChainOpen(false);
    fetchView({ focus, depth: '1', via: lastFocusId.current || undefined }).then((res) => {
      if (mine !== seq.current) return;
      if (res.state === 'notFound') {
        onNotFound?.();
        return;
      }
      if (res.state !== 'ok') {
        setState({ status: 'error' });
        return;
      }
      const view = res.data;
      onLoaded?.(view);
      const byId = Object.fromEntries((view.nodes || []).map((n) => [n.id, n.data]));
      const focusId = view.focusId ? String(view.focusId) : null;
      // Odak varsa doğrudan bağlıları; odak çözülemezse (pozisyonsuz kullanıcı, focus=root) kökler.
      const hasParentEdge = new Set((view.edges || []).map((e) => e.target));
      const reports = focusId
        ? (view.edges || []).filter((e) => e.source === focusId).map((e) => byId[e.target]).filter(Boolean)
        : (view.nodes || []).filter((n) => !hasParentEdge.has(n.id)).map((n) => n.data);
      lastFocusId.current = focusId;
      setState({
        status: 'ok', view, focusId, node: focusId ? byId[focusId] : null, reports, reportsPage: 1,
      });
      const parentId = focusId ? byId[focusId]?.parentPositionId : null;
      if (parentId) loadPeers(String(parentId), focusId, 1, mine);
    });
    // onNotFound sayfadan gelir; her çizimde yeni kimlik alsa da yeniden yükleme sebebi değildir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, loadPeers]);

  const loadMoreReports = useCallback(async () => {
    if (state.status !== 'ok' || !state.focusId) return;
    const mine = seq.current;
    const page = state.reportsPage + 1;
    const res = await fetchChildren(state.focusId, { page });
    if (mine !== seq.current || res.state !== 'ok') return;
    const more = (res.data.nodes || []).map((n) => n.data);
    setState((prev) => ({ ...prev, reports: prev.reports.concat(more), reportsPage: page }));
  }, [state]);

  if (state.status === 'loading') {
    return <Centered data-testid="person-loading"><CircularProgress size={20} />{t('orgChart.loading')}</Centered>;
  }
  if (state.status === 'error') {
    return <div data-testid="person-error"><LoadErrorBanner message={t('orgChart.loadFailed')} /></div>;
  }

  const { view, node, focusId, reports } = state;
  const chain = view.breadcrumb || [];
  const parts = chainOpen ? { head: chain, hidden: [], tail: [] } : chainForDisplay(chain);
  const directTotal = focusId ? (node?.directCount || 0) : (view.rootTotalCount ?? reports.length);
  const remainingReports = Math.max(0, directTotal - reports.length);
  const remainingPeers = Math.max(0, peers.total - peers.items.length);
  const color = colorOf(node);

  const chainCard = (c) => (
    <React.Fragment key={c.positionId}>
      <ChainCard type="button" data-testid={`chain-${c.positionId}`} onClick={() => onFocus?.(String(c.positionId))}>
        <Person data={c} size={28} color={colorOf(c)} />
        <span><b>{label(c)}</b><small>{c.titleName || '-'}</small></span>
      </ChainCard>
      <VLine />
    </React.Fragment>
  );

  const gridCard = (d) => (
    <GridCard key={d.positionId} type="button" data-testid={`report-${d.positionId}`} $vacant={vacantOf(d)}
      onClick={() => onFocus?.(String(d.positionId))}>
      <Person data={d} size={40} color={colorOf(d)} />
      <Who>
        <div><b>{label(d)}</b> {d.isOwn && <You>{t('orgChart.you')}</You>}</div>
        <div>{d.titleName || '-'}</div>
      </Who>
      {d.directCount > 0 && <Count data-testid="report-count">{t('orgChart.peopleCount', { count: d.directCount })}</Count>}
    </GridCard>
  );

  return (
    <Scroll data-testid="person-view">
      <Wrap>
        <Main>
          {node ? (
            <>
              {chain.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} data-testid="person-chain">
                  {parts.head.map(chainCard)}
                  {parts.hidden.length > 0 && (
                    <>
                      <MoreChain type="button" data-testid="chain-more" onClick={() => setChainOpen(true)}>
                        {t('orgChart.moreManagers', { count: parts.hidden.length })}
                      </MoreChain>
                      <VLine />
                    </>
                  )}
                  {parts.tail.map(chainCard)}
                </div>
              )}
              <Hero data-testid="person-hero">
                <Person data={node} size={72} color={color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2>{label(node)}{node.isOwn && <You>{t('orgChart.you')}</You>}</h2>
                  <div style={{ color: '#6B7685', marginTop: 2 }}>{node.titleName || '-'}</div>
                  {node.departmentName && <Chip $color={color}>{node.departmentName}</Chip>}
                  {!vacantOf(node) && node.corporateEmail && (
                    <div style={{ marginTop: 6, fontSize: 13 }} data-testid="person-email">{node.corporateEmail}</div>
                  )}
                  <Stats>
                    <div data-testid="stat-direct"><b>{node.directCount || 0}</b>{t('orgChart.directCount')}</div>
                    <div data-testid="stat-total"><b>{node.totalHeadcount ?? '-'}</b>{t('orgChart.totalTeam')}</div>
                    <div data-testid="stat-peers"><b>{peers.total}</b>{t('orgChart.peerCount')}</div>
                  </Stats>
                  {(node.inManagedScope || node.isOwn) && renderScopeTools?.(node)}
                </div>
              </Hero>
              {reports.length > 0 && <VLine $h={22} />}
            </>
          ) : (
            <SectionHead as="h2" data-testid="person-all-company">{t('orgChart.allCompany')}</SectionHead>
          )}
          {reports.length > 0 && (
            <Section $flush>
              <SectionHead>{t('orgChart.directReports')} <span>· {directTotal}</span></SectionHead>
              <Grid data-testid="person-reports">{reports.map(gridCard)}</Grid>
              {remainingReports > 0 && !focusId && (
                <div style={{ marginTop: 12, color: '#6B7685', fontSize: 13 }} data-testid="roots-more">
                  {t('orgChart.rootsTruncated', { shown: reports.length, total: directTotal })}
                </div>
              )}
              {remainingReports > 0 && focusId && (
                <div style={{ marginTop: 12 }}>
                  <Button variant="ghost" data-testid="reports-more" onClick={loadMoreReports}>
                    {t('orgChart.loadMore', { count: remainingReports })}
                  </Button>
                </div>
              )}
            </Section>
          )}
        </Main>
        {node && (
          <Side data-testid="person-peers">
            <SectionHead>{t('orgChart.peers')} {peers.total > 0 && <span>· {peers.total}</span>}</SectionHead>
            {!node.parentPositionId && <div style={{ color: '#6B7685', fontSize: 13 }}>{t('orgChart.topPosition')}</div>}
            {peers.items.map((d) => (
              <PeerRow key={d.positionId} type="button" data-testid={`peer-${d.positionId}`} onClick={() => onFocus?.(String(d.positionId))}>
                <Person data={d} size={28} color={colorOf(d)} />
                <span><b>{label(d)}</b><small>{d.titleName || '-'}</small></span>
              </PeerRow>
            ))}
            {remainingPeers > 0 && (
              <Button variant="ghost" data-testid="peers-more"
                onClick={() => loadPeers(String(node.parentPositionId), focusId, peers.page + 1, seq.current)}>
                {t('orgChart.loadMore', { count: remainingPeers })}
              </Button>
            )}
            <Button variant="outline" fullWidth data-testid="show-in-tree" style={{ marginTop: 12 }}
              onClick={() => onShowInTree?.(focusId)}>
              {t('orgChart.showInTree')}
            </Button>
          </Side>
        )}
      </Wrap>
    </Scroll>
  );
}
